/**
 * LazyLoadSentinel
 *
 * Invisible bottom-of-grid marker that fires `onIntersect` when it scrolls into view, used by the
 * Range Detail PLP (KINDW-99 / FSD PLP-03) to trigger the next lazy-load batch.
 *
 * The IntersectionObserver `rootMargin` is wide (default 400 px) so the next page is requested
 * before the user actually reaches the bottom — keeps perceived scroll continuous.
 *
 * The component does **not** render itself when `enabled` is false (e.g. no more pages to load) so
 * we don't keep an idle observer attached.
 */
import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RANGE_PLP_LAZY_LOAD_ROOT_MARGIN_PX} from '@amblique/king-living/app/constants';

const LazyLoadSentinel = ({
  enabled = true,
  onIntersect,
  rootMargin = `${RANGE_PLP_LAZY_LOAD_ROOT_MARGIN_PX}px`,
  ...rest
}) => {
  const sentinelRef = useRef(null);
  // Always read the latest callback inside the IntersectionObserver, without re-creating the observer.
  const onIntersectRef = useRef(onIntersect);

  useEffect(() => {
    onIntersectRef.current = onIntersect;
  }, [onIntersect]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const node = sentinelRef.current;

    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onIntersectRef.current?.();
          }
        }
      },
      {root: null, rootMargin, threshold: 0}
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  if (!enabled) return null;

  return (
    <Box
      ref={sentinelRef}
      data-testid="lazy-load-sentinel"
      aria-hidden="true"
      width="100%"
      height="1px"
      {...rest}
    />
  );
};

LazyLoadSentinel.propTypes = {
  enabled: PropTypes.bool,
  onIntersect: PropTypes.func.isRequired,
  rootMargin: PropTypes.string,
};

export default LazyLoadSentinel;
